import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import apiGateWay from "../../serviceHandler";
import Swal from "sweetalert2";

const PaymentResult = () => {
  const [status, setStatus] = useState("loading"); // "loading" | "success" | "failed"
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Extract order reference from query params or session
    const params = new URLSearchParams(location.search);
    const orderRefFromUrl = params.get("orderReference") || params.get("ref");
    const orderReference = orderRefFromUrl || sessionStorage.getItem("orderReference");
    

    if (!orderReference) {
      setStatus("failed");
      showErrorAndRedirect("Order reference not found");
      return;
    }

    verifyPaymentStatus(orderReference);
  }, [location]);

  const verifyPaymentStatus = async (orderReference) => {
    try {
      const response = await apiGateWay.post(
        "/keyconnect/simphony/GetNIPaymentOrderStatus",
        { OrderReference: orderReference }
      );

      if (response.status === 200) {
        const paymentStatus = response.data?.result?._embedded?.payment[0]?.state;
        console.log("Payment Status Response:", paymentStatus, typeof paymentStatus);
        
        // Check if payment was successful
        if (
          paymentStatus == "SUCCESS" ||
          paymentStatus == "PAID" ||
          paymentStatus == "COMPLETED" ||
          paymentStatus == "CAPTURED" ||
          paymentStatus == "PURCHASED"
        ) {
          console.log("Payment Status Response2:", paymentStatus);

          setStatus("success");
          showSuccessAndPostCheck();
        } else if (
          paymentStatus === "PENDING" ||
          paymentStatus === "PROCESSING"
        ) {
          // Payment still processing
          setStatus("loading");
          setTimeout(() => verifyPaymentStatus(orderReference), 3000);
        } else if (paymentStatus == "STARTED") {
          // Payment failed or cancelled
          setStatus("failed");
          showErrorAndRedirect("Payment was cancelled");
        }
        else {
          setStatus("failed");
          showErrorAndRedirect("Payment was not successful");
        }
      } else {
        setStatus("failed");
        showErrorAndRedirect("Payment verification failed");
      }
    } catch (error) {
      console.error("Payment verification error:", error);
      setStatus("failed");
      showErrorAndRedirect("Failed to verify payment status");
    }
  };

  const showSuccessAndPostCheck = () => {
    Swal.fire({
      title: "Success",
      text: "Payment Successful",
      icon: "success",
      confirmButtonColor: "#0f145b",
    }).then((result) => {
      if (result.isConfirmed) {
        createPostCheck();
      }
    });
  };

  const createPostCheck = async () => {
    try {
      const postBodyRaw = sessionStorage.getItem("checkData");
      const generalComment = JSON.parse(sessionStorage.getItem("generalComment"));

      if (!postBodyRaw) {
        Swal.fire({
          title: "Order Information Missing",
          text: "Payment was successful but order details were not found.",
          icon: "error",
          confirmButtonColor: "#0f145b",
        }).then(() => redirectToMenu());
        return;
      }

      const postBody = JSON.parse(postBodyRaw);

      if (generalComment) {
        postBody.GuestRemarks = generalComment;
      }
      postBody.Guest_count = 1;
      postBody.checkname = "EzMenu Guest";

      const postResponse = await apiGateWay.post(
        "keyconnect/simphony/PostCheck",
        postBody
      );

      console.log("PostCheck Response:", postResponse);
      
      // Handle the response safely - check if response.data exists
      const responseData = postResponse?.data || {};
      console.log("Response Data:", responseData);

      // Check if we have a successful response with checkSummary
      const hasCheckSummary = responseData?.result?.checkSummary ? true : false;

      if (!hasCheckSummary) {
        // Get order reference from session storage
        const orderReference = sessionStorage.getItem("orderReference") || "Unknown";
        
        // Extract message from response with safe navigation
        const messageData = responseData?.message || {};
        const errorData = responseData?.error || {};
        
        // Build the message exactly as shown in the image
        // Try to get description from various possible locations
        let description = messageData.description || 
                         errorData.description || 
                         responseData?.description ||
                         "We were unable to complete your order";
        
        // Format the message with reference if available
        const formattedMessage = `${description} (Reference: ${orderReference}). Any payment made has been successfully reversed. Please try again or contact our staff for assistance.`;

        await Swal.fire({
          title: "Order Could Not Be Completed",
          html: `
            <div style="text-align: left; font-family: inherit;">
              <p style="margin-bottom: 16px; line-height: 1.5;">${formattedMessage}</p>
            </div>
          `,
          icon: "error",
          confirmButtonColor: "#0f145b",
          confirmButtonText: "Return to Menu",
          width: '500px',
          padding: '20px'
        });

        // Update payment status as failed
        await updateNIPaymentOrderStatus("Failed", responseData);
        redirectToMenu();
        return;
      }

      // Success case - Update payment status as posted
      await updateNIPaymentOrderStatus("Posted", responseData);
      
      Swal.fire({
        title: "Order Placed ",
        text: "Your order has been placed successfully. Estimated delivery time is approximately 25-30 minutes.",
        icon: "success",
        confirmButtonColor: "#0f145b",
      }).then(() => {
        // Clean up session storage
        sessionStorage.removeItem("cartData");
        sessionStorage.removeItem("checkData");
        sessionStorage.removeItem("orderReference");
        sessionStorage.removeItem("paymentAmount");
        sessionStorage.removeItem("generalComment");
        sessionStorage.removeItem("orderId");
        navigate("/home");
      });

    } catch (error) {
      console.error("Post check error:", error);
      
      // Get order reference from session storage
      const orderReference = sessionStorage.getItem("orderReference") || "Unknown";
      
      // Check if error response exists and extract data safely
      const errorResponse = error?.response?.data || {};
      const errorMessage = error?.response?.data?.message || {};
      const errorDetails = error?.response?.data?.error || {};
      
      console.log("Error Response Data:", errorResponse);
      
      // Build description from available data
      const description = errorMessage.description || 
                         errorDetails.description || 
                         errorResponse.description ||
                         "We were unable to complete your order";
      
      // Format the message exactly as shown in the image
      const formattedMessage = `${description} (Reference: ${orderReference}). Any payment made has been successfully reversed. Please try again or contact our staff for assistance.`;
      
      await Swal.fire({
        title: "Order Could Not Be Completed",
        html: `
          <div style="text-align: left; font-family: inherit;">
            <p style="margin-bottom: 16px; line-height: 1.5;">${formattedMessage}</p>
          </div>
        `,
        icon: "error",
        confirmButtonColor: "#0f145b",
        confirmButtonText: "Return to Menu",
        width: '500px',
        padding: '20px'
      });
      
      // Update payment status as failed with whatever data we have
      await updateNIPaymentOrderStatus("Failed", errorResponse);
      redirectToMenu();
    }
  };

  const updateNIPaymentOrderStatus = async (paymentStatus, responseData = {}) => {
    try {
      const orderReference = sessionStorage.getItem("orderReference");
      const orderId = JSON.parse(sessionStorage.getItem("orderId"));
      
      // Safely extract check details from responseData
      const checkSummary = responseData?.result?.checkSummary || {};
      
      const payload = {
        OrderId: orderId,
        OrderReference: orderReference,
        CheckNumber: checkSummary.checkNumber,
        CheckSequenceNumber: checkSummary.checkSequenceNumber,
        CheckDate: responseData?.result?.orderPosted_Time || new Date().toISOString(),
        POSCheckStatus: paymentStatus,
      };

      console.log("🔹 UpdateNIPaymentOrderStatus payload:", payload);

      const response = await apiGateWay.post(
        "/keyconnect/simphony/UpdateNIPaymentOrderStatus",
        payload
      );

      if (response.status === 200) {
        console.log("✅ UpdateNIPaymentOrderStatus success:", response.data);
        return response.data;
      } else {
        console.warn("⚠️ UpdateNIPaymentOrderStatus failed:", response.status, response.data);
        return null;
      }
    } catch (error) {
      console.error("❌ Error calling UpdateNIPaymentOrderStatus:", error);
      return null;
    }
  };

  const showErrorAndRedirect = (message) => {
    Swal.fire({
      title: "Error!",
      text: message || "Payment verification failed!",
      icon: "error",
      confirmButtonColor: "#0f145b",
    }).then(() => {
      redirectToMenu();
    });
  };

  const redirectToMenu = () => {
    const urlData = JSON.parse(sessionStorage.getItem("urlParams"));
    if (urlData) {
      navigate(
        `/ezmenu/${urlData.tableNumer}/${urlData.rvc}/${urlData.zone}/${urlData.actualTableNumber}`
      );
    } else {
      navigate("/home");
    }
  };

  return (
    <div className="startWrap">
      <div className="start-menu-wrapper">
        {status === "loading" && (
          <div className="text-center p-4">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <h2 className="text-xl font-bold text-gray-700">
              Verifying your payment...
            </h2>
            <p className="text-gray-600 mt-2">Please wait</p>
          </div>
        )}

        {status === "success" && (
          <div className="text-center p-4">
            <div className="text-6xl mb-4">✅</div>
            <h2 className="text-2xl font-bold text-green-600">Payment Successful</h2>
            <p className="mt-2 text-gray-700">Thank you for your purchase!</p>
          </div>
        )}

        {status === "failed" && (
          <div className="text-center p-4">
            <div className="text-6xl mb-4">❌</div>
            <h2 className="text-2xl font-bold text-red-600">Payment Failed</h2>
            <p className="mt-2 text-gray-700">
              Unfortunately, we couldn't process your payment.
            </p>
            <p className="text-sm text-gray-500 mt-1">
              Any payment made has been successfully reversed.
            </p>
            <p className="text-gray-600 mt-3">
              You will be redirected to the menu shortly.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentResult;